import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { FiArrowLeft, FiLock, FiMessageCircle, FiMessageSquare, FiPlus, FiTrash2, FiUnlock } from "react-icons/fi";
import {
  createTopic,
  createTopicCategory,
  deleteTopic,
  deleteTopicCategory,
  getTopic,
  getTopics,
  lockTopic,
  TopicCategory,
  TopicDetail,
  TopicSummary,
  WallHeader,
  WallPost
} from "@lubbezposrednio-frontend/core";
import WallPostCard from "./WallPostCard";
import { CreatePostModal } from "./CreatePostModal";
import { mediaUrl, relTime } from "./wallShared";

const TopicsPanel: FC<{ groupId: string; canManage: boolean; header: WallHeader; initialTopicId?: string | null }> = ({ groupId, canManage, header, initialTopicId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<TopicCategory[]>([]);
  const [openTopicId, setOpenTopicId] = useState<string | null>(initialTopicId ?? null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  // Wejście w konkretny temat z railu / deep-linku.
  useEffect(() => { if (initialTopicId) setOpenTopicId(initialTopicId); }, [initialTopicId]);

  const load = async () => {
    try {
      const data = await getTopics(groupId);
      setCategories(data.categories);
    } catch {
      toast({ title: "Nie udało się wczytać tematów", status: "error", isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [groupId]);

  if (openTopicId) {
    return <TopicView groupId={groupId} topicId={openTopicId} header={header} onBack={() => { setOpenTopicId(null); void load(); }} />;
  }

  const removeCategory = async (id: string) => {
    if (!window.confirm("Usunąć kategorię wraz ze wszystkimi tematami?")) return;
    try {
      await deleteTopicCategory(groupId, id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch {
      toast({ title: "Nie udało się usunąć kategorii", status: "error", isClosable: true });
    }
  };

  if (loading) return <Flex justify="center" py="60px"><Spinner color="primary" /></Flex>;

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="16px"
        wrap="wrap"
        gap="8px"
        bg="white"
        borderRadius="16px"
        border="1px solid rgba(67,0,152,0.08)"
        boxShadow="0 6px 20px rgba(67,0,152,0.05)"
        p="14px 16px"
      >
        <HStack spacing="9px">
          <Flex w="34px" h="34px" borderRadius="10px" bg="rgba(67,0,152,0.08)" align="center" justify="center">
            <Icon as={FiMessageSquare} color="primary" boxSize="16px" />
          </Flex>
          <Text fontSize="18px" fontWeight="800" color="primary">Tematy</Text>
        </HStack>
        <HStack spacing="8px">
          {canManage && (
            <Button size="sm" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="11px" fontWeight="700" leftIcon={<FiPlus />} onClick={() => setCategoryOpen(true)}>
              Kategoria
            </Button>
          )}
          <Button size="sm" bg="primary" color="white" borderRadius="11px" fontWeight="700" leftIcon={<FiPlus />} _hover={{ bg: "#360079" }} onClick={() => setComposeOpen(true)} isDisabled={categories.length === 0}>
            Nowy temat
          </Button>
        </HStack>
      </Flex>

      {categories.length === 0 ? (
        <Flex direction="column" align="center" py="50px" bg="white" borderRadius="18px" border="1px dashed rgba(67,0,152,0.2)" gap="10px">
          <Icon as={FiMessageSquare} boxSize="28px" color="primary" />
          <Text fontWeight="700" color="primary">Brak tematów</Text>
          <Text fontSize="14px" color="gray.500" textAlign="center" px="20px">
            {canManage ? "Utwórz pierwszą kategorię, a potem temat do dyskusji." : "Organizator nie utworzył jeszcze żadnej kategorii."}
          </Text>
        </Flex>
      ) : (
        <VStack align="stretch" spacing="18px">
          {categories.map(cat => (
            <Box key={cat.id}>
              <Flex justify="space-between" align="center" mb="8px">
                <Text fontSize="13px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">{cat.name}</Text>
                {canManage && (
                  <Icon as={FiTrash2} boxSize="13px" color="gray.300" cursor="pointer" _hover={{ color: "#e51f44" }} onClick={() => removeCategory(cat.id)} aria-label="Usuń kategorię" />
                )}
              </Flex>
              {cat.topics.length === 0 ? (
                <Text fontSize="13px" color="gray.400" pl="2px" pb="6px">Brak tematów w tej kategorii.</Text>
              ) : (
                <VStack align="stretch" spacing="8px">
                  {cat.topics.map(t => <TopicRow key={t.id} topic={t} onOpen={() => setOpenTopicId(t.id)} />)}
                </VStack>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {composeOpen && (
        <NewTopicModal
          groupId={groupId}
          categories={categories}
          onClose={() => setComposeOpen(false)}
          onCreated={topic => { setComposeOpen(false); setOpenTopicId(topic.id); }}
        />
      )}

      {categoryOpen && (
        <NewCategoryModal
          groupId={groupId}
          onClose={() => setCategoryOpen(false)}
          onCreated={cat => { setCategoryOpen(false); setCategories(prev => [...prev, cat]); }}
        />
      )}
    </Box>
  );
};

const TopicRow: FC<{ topic: TopicSummary; onOpen: () => void }> = ({ topic, onOpen }) => (
  <Flex
    onClick={onOpen}
    cursor="pointer"
    bg="white"
    borderRadius="14px"
    border="1px solid rgba(67,0,152,0.08)"
    p="12px 14px"
    align="center"
    gap="12px"
    _hover={{ borderColor: "rgba(67,0,152,0.2)", boxShadow: "0 4px 14px rgba(67,0,152,0.05)" }}
  >
    <Avatar size="sm" name={topic.author.name} src={mediaUrl(topic.author.avatar_url ?? undefined)} />
    <Box flex="1" minW={0}>
      <HStack spacing="6px">
        <Text fontWeight="700" color="gray.800" noOfLines={1}>{topic.title}</Text>
        {topic.locked && <Badge colorScheme="gray" display="flex" alignItems="center" gap="3px"><Icon as={FiLock} boxSize="9px" /> Zamknięty</Badge>}
      </HStack>
      <Text fontSize="12px" color="gray.400" noOfLines={1}>
        {topic.author.name} · {topic.posts_count} {topic.posts_count === 1 ? "post" : "postów"} · {relTime(topic.last_activity)}
      </Text>
    </Box>
    <Icon as={FiMessageSquare} boxSize="15px" color="gray.300" />
  </Flex>
);

const NewTopicModal: FC<{ groupId: string; categories: TopicCategory[]; onClose: () => void; onCreated: (t: TopicDetail) => void }> = ({ groupId, categories, onClose, onCreated }) => {
  const toast = useToast();
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!categoryId || title.trim().length < 3) {
      toast({ title: "Wybierz kategorię i podaj tytuł (min. 3 znaki)", status: "error", isClosable: true });
      return;
    }
    setSubmitting(true);
    try {
      const topic = await createTopic(groupId, categoryId, title.trim(), body.trim() || undefined);
      onCreated(topic);
    } catch {
      toast({ title: "Nie udało się utworzyć tematu", status: "error", isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="18px" mx="16px">
        <ModalHeader color="primary" fontWeight="800">Nowy temat</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing="12px">
            <FormControl>
              <FormLabel fontSize="13px" color="gray.600" fontWeight="700">Kategoria</FormLabel>
              <Select value={categoryId} onChange={e => setCategoryId(e.target.value)} borderRadius="12px">
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="13px" color="gray.600" fontWeight="700">Tytuł</FormLabel>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="O czym dyskutujemy?" borderRadius="12px" maxLength={200} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="13px" color="gray.600" fontWeight="700">Pierwszy post (opcjonalnie)</FormLabel>
              <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Rozpocznij dyskusję…" borderRadius="12px" rows={4} maxLength={5000} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} borderRadius="12px" fontWeight="600" isDisabled={submitting}>Anuluj</Button>
          <Button onClick={submit} isLoading={submitting} bg="primary" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#360079" }}>Utwórz</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const NewCategoryModal: FC<{ groupId: string; onClose: () => void; onCreated: (c: TopicCategory) => void }> = ({ groupId, onClose, onCreated }) => {
  const toast = useToast();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const value = name.trim();
    if (value.length < 2) {
      toast({ title: "Podaj nazwę kategorii (min. 2 znaki)", status: "error", isClosable: true });
      return;
    }
    setSubmitting(true);
    try {
      onCreated(await createTopicCategory(groupId, value));
    } catch {
      toast({ title: "Nie udało się dodać kategorii", status: "error", isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="18px" mx="16px">
        <ModalHeader color="primary" fontWeight="800">Nowa kategoria</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel fontSize="13px" color="gray.600" fontWeight="700">Nazwa</FormLabel>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="np. Ogłoszenia, Pytania…" borderRadius="12px" maxLength={100} autoFocus onKeyDown={e => { if (e.key === "Enter") submit(); }} />
          </FormControl>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} borderRadius="12px" fontWeight="600" isDisabled={submitting}>Anuluj</Button>
          <Button onClick={submit} isLoading={submitting} bg="primary" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#360079" }}>Dodaj</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ── Widok pojedynczego tematu = mini-ściana zawężona do wątku (te same komponenty co Aktualności) ──

const TopicView: FC<{ groupId: string; topicId: string; header: WallHeader; onBack: () => void }> = ({ groupId, topicId, header, onBack }) => {
  const toast = useToast();
  const [topic, setTopic] = useState<TopicDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const composer = useDisclosure();

  const load = async () => {
    try {
      setTopic(await getTopic(groupId, topicId));
    } catch {
      toast({ title: "Nie udało się wczytać tematu", status: "error", isClosable: true });
      onBack();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [topicId]);

  if (loading || !topic) return <Flex justify="center" py="60px"><Spinner color="primary" /></Flex>;

  const canPost = !topic.locked || topic.can_manage;
  const updatePost = (postId: string, updater: (p: WallPost) => WallPost) =>
    setTopic(prev => (prev ? { ...prev, posts: prev.posts.map(p => (p.id === postId ? updater(p) : p)) } : prev));

  const toggleLock = async () => {
    try {
      setTopic(await lockTopic(groupId, topicId));
    } catch {
      toast({ title: "Nie udało się zmienić stanu tematu", status: "error", isClosable: true });
    }
  };

  const removeTopic = async () => {
    if (!window.confirm("Usunąć ten temat wraz z postami?")) return;
    try {
      await deleteTopic(groupId, topicId);
      onBack();
    } catch {
      toast({ title: "Nie udało się usunąć tematu", status: "error", isClosable: true });
    }
  };

  return (
    <Box>
      <Button variant="ghost" size="sm" color="gray.500" leftIcon={<FiArrowLeft />} onClick={onBack} mb="12px" fontWeight="700">
        Wszystkie tematy
      </Button>

      <Box bg="white" borderRadius="16px" border="1px solid rgba(67,0,152,0.08)" p="16px" mb="16px">
        <Flex justify="space-between" align="flex-start" gap="10px">
          <Box minW={0}>
            <HStack spacing="8px">
              <Text fontSize="19px" fontWeight="800" color="primary">{topic.title}</Text>
              {topic.locked && <Badge colorScheme="gray" display="flex" alignItems="center" gap="3px"><Icon as={FiLock} boxSize="9px" /> Zamknięty</Badge>}
            </HStack>
            <Text fontSize="12px" color="gray.400" mt="2px">Założył(a) {topic.author.name} · {relTime(topic.created_at)}</Text>
          </Box>
          <HStack spacing="2px" flexShrink={0}>
            {topic.can_manage && (
              <IconButton aria-label={topic.locked ? "Otwórz" : "Zamknij"} icon={topic.locked ? <FiUnlock /> : <FiLock />} onClick={toggleLock} size="sm" variant="ghost" color="gray.400" />
            )}
            {topic.can_delete && (
              <IconButton aria-label="Usuń" icon={<FiTrash2 />} onClick={removeTopic} size="sm" variant="ghost" color="gray.400" _hover={{ color: "#e51f44" }} />
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Kompozer — ten sam modal co na ścianie (Napisz post) */}
      {canPost && (
        <Box bg="white" borderRadius="16px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p="14px 16px" mb="16px">
          <HStack spacing="11px">
            <Avatar size="sm" boxSize="40px" name={header.me.name} src={mediaUrl(header.me.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" />
            <Box as="button" onClick={composer.onOpen} flex="1" textAlign="left" bg="#F4F0FB" _hover={{ bg: "rgba(67,0,152,0.08)" }} borderRadius="full" px="18px" py="11px" transition="all 0.12s">
              <Text color="gray.500" fontSize="15px">Napisz post w temacie…</Text>
            </Box>
            <IconButton aria-label="Napisz post" icon={<FiMessageCircle />} onClick={composer.onOpen} variant="ghost" color="primary" />
          </HStack>
        </Box>
      )}

      <VStack align="stretch" spacing="16px">
        {topic.posts.length === 0 && <Text fontSize="14px" color="gray.400" textAlign="center" py="20px">Brak postów — napisz pierwszy.</Text>}
        {topic.posts.map(p => (
          <WallPostCard
            key={p.id}
            post={p}
            groupId={groupId}
            isModerator={topic.can_manage}
            onChange={updater => updatePost(p.id, updater)}
            onRemove={() => setTopic(prev => (prev ? { ...prev, posts: prev.posts.filter(x => x.id !== p.id) } : prev))}
          />
        ))}
      </VStack>

      {!canPost && (
        <Flex align="center" gap="8px" bg="rgba(67,0,152,0.04)" borderRadius="12px" p="12px" justify="center" mt="16px">
          <Icon as={FiLock} color="gray.400" />
          <Text fontSize="13px" color="gray.500">Temat zamknięty — nie można dodawać postów.</Text>
        </Flex>
      )}

      <CreatePostModal
        isOpen={composer.isOpen}
        onClose={composer.onClose}
        groupId={groupId}
        groupName={topic.title}
        me={header.me}
        topicId={topicId}
        onCreated={post => setTopic(prev => (prev ? { ...prev, posts: [...prev.posts, post] } : prev))}
      />
    </Box>
  );
};

export default TopicsPanel;
